import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { isBilling2023V2Subscription } from '@mm/state/model/SubscriptionModel';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { enhancedPlansSelector } from '@mm/state/selectors/subscriptionSelectors';
import { useSelector } from 'react-redux';
import {
  activeFacebookPageSelector,
  getPageWithBillingInfo,
} from '@mm/state/selectors/facebookPageSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { SettingsRoutes } from '../../../../../routes/settings-routes';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { notification } from 'antd';
import {
  activeCompanySelector,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import React, { ReactNode, useEffect, useState } from 'react';
import imageSrc from 'assets/cancel-subscription.png';
import {
  PlanBillingPeriodBE,
  PlanSubtypes,
  planSubtypesByType,
} from '@mm/state/interfaces/subscription';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { useFreeTrialUpgradeModal } from '../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { BookDemoButton } from '../../../../../common/buttons/BookDemoButton/BookDemoButton';
import styles from './BillingPlans.module.css';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import {
  BILLING_PAGE_UTM,
  BOOK_DEMO_NON_ENTERPRISE,
} from '../../../XRayOnboarding/WebsiteReportSummary/constants';
import classNames from 'classnames';
import leadMagnetIcon from 'assets/icons/lead-magnet-icon.svg';
import likeIcon from 'assets/icons/like-outlined.svg';
import userIcon from 'assets/icons/user.svg';

export type BillingPlanCardProps = {
  title: string;
  header?: null | string;
  price: string | ReactNode;
  pricePeriod?: string;
  buttonName?: string;
  onClick?: () => void;
  buttonComponent?: (disabled?: boolean) => ReactNode;
  planSubtypes?: PlanSubtypes[];
  points?: string[];
  pointsIcons?: string[];
  pointsTitle?: string;
  secondaryPoints?: (string | ReactNode)[];
  costs?: string[] | ReactNode;
  tag?: string;
  cardBgColor?: string;
  tagBgColor?: string;
  hide?: boolean;
  isCustom?: boolean;
  isFreeTrial?: boolean;
  currentTag?: boolean;
  planCode?: string;
  downgrade?: boolean;
};

export type ManageSubsctiptionRouteParams = {
  tabId: string;
  pageId: string;
};

export const useBillingPlans = ({
  isAnnual,
  onBillingSuccess,
  hideFreePlan,
  landmineOpenId,
}) => {
  const plans = useSelector(enhancedPlansSelector);
  const basicFacebookPages = useSelector(
    (state: StoreState) => state.connectedPage.basicPagesList,
  );
  const plansMapping = useSelector(
    (state: StoreState) => state.subscription.plansMapping,
  );
  const company = useSelector(activeCompanySelector);
  const isAgency = company?.companyAttributes?.agency;
  const companyId = useSelector(selectActiveCompanyId);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const { tabId, pageId } = useParams<ManageSubsctiptionRouteParams>();
  const location = useLocation();
  const history = useHistory();
  const activeFacebookPage = pageId
    ? basicFacebookPages.find(p => p.id === Number(pageId))
    : facebookPage;

  const currentPlan = plans.find(plan =>
    tabId
      ? plan.planCode === tabId
      : activeFacebookPage?.subscriptions?.find(
          sub => sub.planCode === plan.planCode,
        ),
  );

  const isFreePlan = !activeFacebookPage?.subscriptions?.length;
  const iscurrentTeamCustomPlan = !!currentPlan?.planSubtypes.find(
    subtype => subtype === PlanSubtypes.teamCustom,
  );
  const isManageSubscriptionsPage = false;

  const canCancelSubscription =
    !isFreePlan &&
    isManageSubscriptionsPage &&
    !(
      activeFacebookPage.subscription?.state === 'canceled' ||
      activeFacebookPage.downgradePending
    ) &&
    !activeFacebookPage.redemption?.freeTrial;

  const plansToDowngrade = plans.filter(plan =>
    currentPlan?.downgradePlansIds.includes(plan.id),
  );

  const { isFreeTrialPromotionActive } = useFreeTrialUpgradeModal();

  const { closeModal, openBillingModal, openNewInfoModal } = useModalActions();

  const { retrieveBasicListByCompany } = useConnectedPageCRUDActions();
  const subscriptionCRUDActions = useSubscriptionCRUDActions();
  const { update } = useCompanyCRUDActions();

  const [isAwaitingCancellation, setIsAwaitingCancellation] = useState(false);

  useEffect(() => {
    const getSubscriptionCancellationStatus = async () => {
      if (activeFacebookPage?.subscription?.id) {
        const hasCancels =
          await subscriptionCRUDActions.getCurrentSubscriptionCancels(
            activeFacebookPage.subscription.id,
          );

        if (hasCancels) {
          setIsAwaitingCancellation(true);
        }
      }
    };

    if (activeFacebookPage?.subscription?.id) {
      getSubscriptionCancellationStatus();
    }
  }, [activeFacebookPage?.subscription?.id]);

  const handleBack = () => {
    history.push(SettingsRoutes.billing.getLink());
  };

  const onRequestEnteriprisePlanScheduled = async () => {
    await update({
      id: companyId,
      bookAnEnterprise: true,
    });
    if (isManageSubscriptionsPage) {
      handleBack();
    } else {
      closeModal();
    }
  };

  const bookDemoButton = (disabled: boolean) => (
    <BookDemoButton
      button={disabled ? 'Current Plan' : 'Book a call'}
      className={classNames(styles.button, {
        [styles.disabledButton]: disabled,
      })}
      calendlyUrl={BOOK_DEMO_NON_ENTERPRISE}
      utm={BILLING_PAGE_UTM('scheduleademo')}
      onEventScheduled={onRequestEnteriprisePlanScheduled}
      show
    />
  );

  const getCurrentPlan = (
    planSubtypes: PlanSubtypes[],
    billingPeriod: PlanBillingPeriodBE,
  ) => {
    const currentPlan = plans.find(
      p =>
        planSubtypes.every(subtype => p.planSubtypes.includes(subtype)) &&
        p.billingPeriod === billingPeriod,
    );
    return { currentPlan };
  };

  const getPlanPrice = (
    planSubtypes: PlanSubtypes[],
    billingPeriod: PlanBillingPeriodBE,
  ) => {
    const { currentPlan } = getCurrentPlan(planSubtypes, billingPeriod);
    return (currentPlan?.unitAmountInCents || 0) / 100;
  };

  const upgradeToPlan = async (
    planSubtypes: PlanSubtypes[],
    billingPeriod: PlanBillingPeriodBE,
  ) => {
    let enhancedBillingPage;

    if (!activeFacebookPage && !basicFacebookPages.length) {
      const res = await retrieveBasicListByCompany(companyId);
      enhancedBillingPage = getPageWithBillingInfo(
        res?.[0],
        plans,
        plansMapping,
      );
    } else {
      enhancedBillingPage = activeFacebookPage;
    }

    const subscriptionId = enhancedBillingPage?.subscription
      ? enhancedBillingPage.subscription.id
      : undefined;

    const { currentPlan } = getCurrentPlan(planSubtypes, billingPeriod);
    const downgrade = plansToDowngrade.find(
      plan => currentPlan?.planCode === plan.planCode,
    );

    if (
      !isBilling2023V2Subscription(enhancedBillingPage?.subscription) &&
      !isFreePlan &&
      !isAgency
    ) {
      openNewInfoModal({
        title: 'Contact Support',
        description: (
          <div>
            Please contact support at{''}{' '}
            <a
              className="pause-subscription-modal-link"
              href="mailto:cs@customers.ai"
            >
              cs@customers.ai
            </a>
            <br />
            to upgrade your subscription
          </div>
        ),
        image: imageSrc,
        button: 'Ok',
        onClick: () => closeModal(),
      });
      return;
    }

    if (currentPlan && enhancedBillingPage) {
      await openBillingModal({
        facebookPage: enhancedBillingPage,
        plan: currentPlan,
        landmineOpenId,
        manualRedirect: true,
        subscription: enhancedBillingPage?.subscription,
        upgradeFromSubscriptionId: !downgrade ? subscriptionId : undefined,
        downgradeFromSubscriptionId: downgrade ? subscriptionId : undefined,
        onSuccessBilling: onBillingSuccess,
        onPlanCreatedFailed: () =>
          notification.error({
            message: 'Something went wrong',
            description:
              'Subscription is not created. Credit card is not charged.',
          }),
        isMonthly: true,
        hideAnnualToggle: true, // annual plans are not implemented properly on BE, for now hide them
      });
    }
  };

  const planPeriod = isAnnual
    ? PlanBillingPeriodBE.year
    : PlanBillingPeriodBE.month;

  const billingPlans: BillingPlanCardProps[] = [
    {
      title: currentPlan?.shortName || '',
      price: `$${(currentPlan?.unitAmountInCents || 0) / 100}`,
      pricePeriod: currentPlan?.billingPeriod === 'month' ? '/mo' : 'year',
      buttonName: 'Current Plan',
      planSubtypes: currentPlan?.planSubtypes,
      hide: !isManageSubscriptionsPage || !currentPlan || isAgency,
    },
    {
      title: 'Free',
      price: '$0',
      pricePeriod: '',
      buttonName: 'Sign Up Free',
      onClick: () => {
        onBillingSuccess?.();
        closeModal();
      },
      points: ['50 Leads Included', '150 Email Sends Included'],
      costs: ['up to 5,000 leads'],
      hide: hideFreePlan,
    },
    {
      title: 'Free Trial',
      header: null,
      price: '',
      buttonName: 'Current Plan',
      onClick: () => {
        onBillingSuccess?.();
        closeModal();
      },
      costs: (
        <div>
          <span style={{ fontWeight: 500 }}>TRY IT FREE</span> up to 500 leads
        </div>
      ),
      secondaryPoints: !isAgency
        ? ['Capture, contact, and convert contacts!']
        : [],
      tag: 'Up to 500 leads FREE',
      tagBgColor: '#389E0D',
      hide: !isFreeTrialPromotionActive,
      isFreeTrial: true,
    },
    {
      title: 'Starter',
      price: `$${getPlanPrice(planSubtypesByType.starterV2, planPeriod)}`,
      pricePeriod: isAnnual ? '/year' : '/mo',
      buttonName: !isFreePlan ? 'Upgrade' : 'Get Started',
      planSubtypes: planSubtypesByType.starterV2,
      onClick: () => upgradeToPlan(planSubtypesByType.starterV2, planPeriod),
      buttonComponent: bookDemoButton,
      points: ['X-Ray Pixel', 'Advanced X-Ray settings'],
      secondaryPoints: [
        'Email automations',
        'Signs of life detector',
        'Suppression lists',
        'AI copywriting',
      ],
      costs: ['$0.23 per contact captured'],
      hide: iscurrentTeamCustomPlan || isAgency,
    },
    {
      title: 'Pro',
      price: `$${getPlanPrice(planSubtypesByType.proV2, planPeriod)}`,
      pricePeriod: isAnnual ? '/year' : '/mo',
      buttonName: !isFreePlan ? 'Upgrade' : 'Get Started',
      planSubtypes: planSubtypesByType.proV2,
      onClick: () => upgradeToPlan(planSubtypesByType.proV2, planPeriod),
      buttonComponent: bookDemoButton,
      pointsTitle: 'Everything in Starter and:',
      points: [
        'B2B Capture',
        'Contacts Enrichment',
        'Restore - Ad retargeting sync ',
      ],
      secondaryPoints: ['Email deliverability tools', 'Unlimited integrations'],
      costs: ['$0.18 per contact captured'],
      tag: 'Most popular',
      tagBgColor: '#63A4C9',
      hide: iscurrentTeamCustomPlan || isAgency,
    },
    {
      title: 'Agency Starter',
      price: `$${getPlanPrice(planSubtypesByType.agencyLevel1, planPeriod)}`,
      pricePeriod: isAnnual ? '/year' : '/month',
      buttonName: 'Upgrade to Plan',
      planSubtypes: planSubtypesByType.agencyLevel1,
      onClick: () => upgradeToPlan(planSubtypesByType.agencyLevel1, planPeriod),
      buttonComponent: !isFreePlan ? bookDemoButton : undefined,
      points: ['500 X-Ray Leads Included monthly', '3 sub-client accounts'],
      pointsIcons: [leadMagnetIcon, userIcon],
      secondaryPoints: [],
      costs: ['$0.20 per X-Ray Lead'],
      hide: !isAgency,
    },

    {
      title: 'Agency Growth',
      price: `$${getPlanPrice(planSubtypesByType.agencyLevel2, planPeriod)}`,
      pricePeriod: isAnnual ? '/year' : '/month',
      buttonName: 'Upgrade to Plan',
      planSubtypes: planSubtypesByType.agencyLevel2,
      onClick: () => upgradeToPlan(planSubtypesByType.agencyLevel2, planPeriod),
      buttonComponent: !isFreePlan ? bookDemoButton : undefined,
      points: ['1,500 X-Ray Leads Included monthly', '10 sub-client accounts'],
      pointsIcons: [leadMagnetIcon, userIcon],
      secondaryPoints: [],
      costs: ['$0.18 per X-Ray Lead'],
      hide: !isAgency,
    },
    {
      title: 'Agency Unlimited',
      price: `$${getPlanPrice(planSubtypesByType.agencyLevel3, planPeriod)}`,
      pricePeriod: isAnnual ? '/year' : '/month',
      buttonName: 'Upgrade to Plan',
      planSubtypes: planSubtypesByType.agencyLevel3,
      onClick: () => upgradeToPlan(planSubtypesByType.agencyLevel3, planPeriod),
      buttonComponent: !isFreePlan ? bookDemoButton : undefined,
      points: [
        '3,500 X-Ray Leads Included monthly',
        '50 sub-client accounts',
        'Dedicated Success Rep',
      ],
      pointsIcons: [leadMagnetIcon, userIcon, likeIcon],
      secondaryPoints: [],
      costs: ['$0.16 per X-Ray Lead'],
      hide: !isAgency,
    },
    {
      title: 'Advanced',
      price: '$799',
      pricePeriod: '/mo',
      buttonComponent: bookDemoButton,
      pointsTitle: 'Everything in Pro and:',
      secondaryPoints: [
        'Customer success manager',
        'Enterprise email sending infrastructure',
      ],
      costs: ['$0.13 per contact captured'],
      isCustom: true,
      hide: isAgency,
    },
  ];

  return {
    billingPlans,
    activeFacebookPage,
    currentPlan,
    tabId,
    pageId,
    plansToDowngrade,
    canCancelSubscription,
    isAwaitingCancellation,
    isManageSubscriptionsPage,
    getCurrentPlan,
  };
};
